import { Input } from '@fluentui/react-components';
import { Checkmark20Regular, Dismiss20Regular } from '@fluentui/react-icons';
import { asUTC, Holiday } from '@mnspro-cloud/services';
import moment from 'moment-timezone';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../Button';
import { Flex } from '../../../../Flex';
import { LocalizedDatePicker } from '../../../../LocalizedDatePicker/LocalizedDatePicker';
import { Text } from '../../../../Text';
import holidayStyle from '../HolidayComponent/HolidayComponent.module.css';
import style from './EditHolidayComponent.module.css';

export interface EditHolidayComponentProps extends Holiday {
  updateClicked?: (updatedModel: Holiday) => void;
  cancelClicked?: () => void;
}

export const EditHolidayComponent: FC<EditHolidayComponentProps> = (props) => {
  const { name, start, end, updateClicked, cancelClicked } = props;
  const { t } = useTranslation();

  const noTitleError = useMemo(
    () => t('name_is_required', { defaultValue: 'Der Name muss ausgefüllt sein.' }),
    [t],
  );
  const timeSequenceError = useMemo(
    () =>
      t('start_date_must_be_before_end_date', {
        defaultValue: 'Das Startdatum muss vor dem Enddatum liegen.',
      }),
    [],
  );

  const [model, setModel] = useState<Holiday>({ name, start, end });
  const [titleErrors, setTitleErrors] = useState<string[]>([]);
  const [timeErrors, setTimeErrors] = useState<string[]>([]);

  const formIsValid = titleErrors.length === 0 && timeErrors.length === 0;

  useEffect(() => {
    validate();
  }, [model]);

  const handleInputChange = (newName: string) => setModel({ ...model, name: newName });

  const handleStartDateChanged = (newDate: string) => setModel({ ...model, start: newDate });

  const handleEndDateChanged = (newDate: string) => setModel({ ...model, end: newDate });

  const handleConfirm = () => updateClicked?.(model);
  const handleCancel = () => {
    setModel({ name, start, end });
    cancelClicked?.();
  };

  const validate = () => {
    const nameErrors = [];
    const tErrors = [];
    if (!model.name || model.name.length === 0) {
      nameErrors.push(noTitleError);
    }
    if (moment(model.end).isBefore(model.start)) {
      tErrors.push(timeSequenceError);
    }
    setTitleErrors(nameErrors);
    setTimeErrors(tErrors);
  };

  return (
    <Flex
      gap="0.5rem"
      className={[holidayStyle.outer, style.outer].join(' ')}
      align="center"
      data-testid="editHolidayComponent"
    >
      <Flex direction="column" gap="0.5rem" fill align="center">
        <Flex gap="0.5rem" justify="center">
          <Text weight="bold">{useMemo(() => t('name', { defaultValue: 'Name' }), [t])}</Text>
          <Input
            appearance="filled-darker"
            value={model.name}
            onChange={(_, data) => handleInputChange(data?.value ?? '')}
            placeholder={t('holidayName', { defaultValue: 'Ferientitel' })}
          />
        </Flex>
        {titleErrors.map((error, index) => (
          <Text color="danger" key={index}>
            {error}
          </Text>
        ))}
        <Flex gap="0.5rem" className={style.mobileLineBreak}>
          <Flex gap="0.5rem" align="center">
            <Text weight="bold">{useMemo(() => t('from', { defaultValue: 'Von' }), [t])}</Text>
            <LocalizedDatePicker
              allowManualInput={false}
              onDateChange={(_, data) => {
                if (data?.value) {
                  handleStartDateChanged(asUTC(moment(data?.value as Date).startOf('D')));
                } else {
                  handleEndDateChanged(model.start);
                }
              }}
              selectedDate={new Date(model.start)}
            />
          </Flex>
          <Flex gap="0.5rem" align="center" className={style.mobileSpace}>
            <Text weight="bold">{useMemo(() => t('to', { defaultValue: 'Bis' }), [t])}</Text>
            <LocalizedDatePicker
              allowManualInput={false}
              onDateChange={(_, data) => {
                if (data?.value) {
                  handleEndDateChanged(asUTC(moment(data?.value as Date).endOf('D')));
                } else {
                  handleEndDateChanged(model.end);
                }
              }}
              selectedDate={new Date(model.end)}
              minDate={new Date(model.start)}
            />
          </Flex>
        </Flex>
        {timeErrors.map((error, index) => (
          <Text color="danger" key={index}>
            {error}
          </Text>
        ))}
      </Flex>
      <Flex direction="column">
        <Button
          appearance="transparent"
          icon={<Checkmark20Regular />}
          onClick={handleConfirm}
          disabled={!formIsValid}
          data-testid="sideMenuConfirmButton"
          aria-label={t('settings:classbook:holidays:confirm_a11y', { defaultValue: 'Bestätigen' })}
        />
        <Button
          appearance="transparent"
          icon={<Dismiss20Regular />}
          onClick={handleCancel}
          disabled={!formIsValid}
          data-testid="sideMenuCancelButton"
          aria-label={t('settings:classbook:holidays:cancel_a11y', { defaultValue: 'Abbrechen' })}
        />
      </Flex>
    </Flex>
  );
};
